<template>
  <!-- Phones only: whenever the page's own hero buttons (`watch`) are off screen, above or still
       below the first screen, keep "Start free trial" in thumb reach. Hidden again over anything matching `hide` (sections that
       carry their own buttons, and the footer) and while the cookie banner needs an answer.
       Fixed position, so it never changes the page height. Same look as HomeStickyCta. -->
  <div class="phone-sticky-cta" :class="{ on: visible }" :aria-hidden="!visible">
    <NuxtLink
      class="psc-secondary"
      :to="localePath('/pricing')"
      :tabindex="visible ? 0 : -1"
      data-conversion-event="pricing_click"
      :data-conversion-label="`${label} sticky pricing`"
      :data-conversion-location="location"
    >
      {{ localeUi.nav.pricing }}
    </NuxtLink>
    <NuxtLink
      v-if="sales"
      class="psc-primary"
      :to="localePath('/contact-sales')"
      :tabindex="visible ? 0 : -1"
      data-conversion-event="demo_request_click"
      :data-conversion-label="`${label} sticky talk to sales`"
      :data-conversion-location="location"
    >
      Talk to sales
    </NuxtLink>
    <a
      v-else
      class="psc-primary"
      :href="addReferralToUrl(href)"
      :tabindex="visible ? 0 : -1"
      data-conversion-event="start_free_click"
      :data-conversion-label="`${label} sticky CTA`"
      :data-conversion-location="location"
    >
      {{ localeUi.startFree }}
    </a>
    <TrialDisclosure v-if="!sales" compact centered :plan="plan" class="psc-note" />
  </div>
</template>

<script setup>
import { HERO_BUTTON_THRESHOLDS, createHideTracker, heroOnScreenFrom, heroTarget, stickyVisible } from "~/utils/phoneStickyCta";

const props = defineProps({
  // Sign-up URL of the page's hero button (same plan, so the bar never changes the offer).
  href: { type: String, default: "https://app.workspace369.com/signup" },
  plan: { type: String, default: "" },
  // Pages whose hero leads with "Talk to sales" (no plan to quote) get that button instead of a trial.
  sales: { type: Boolean, default: false },
  // Analytics names: "<label> sticky CTA" at <location>, next to the page's own CTA labels.
  label: { type: String, required: true },
  location: { type: String, required: true },
  // The hero's visible button row. Watch the row, not "the first trial link": a hidden link earlier
  // in the hero (the home page's referral strip) has a zero box at the top and keeps the bar off.
  watch: { type: String, required: true },
  hide: { type: String, default: "[data-sticky-cta-hide], footer" },
});

const { addReferralToUrl } = useReferral();
const { localeUi, localePath } = useWorkspaceLocale();
const { needsBanner } = useCookieConsent();

// Assume the hero buttons are on screen until the observer says otherwise, so the bar never flashes in.
const heroOnScreen = ref(true);
const overOwnCta = ref(false);
const visible = computed(() =>
  stickyVisible({ heroOnScreen: heroOnScreen.value, overOwnCta: overOwnCta.value, needsBanner: needsBanner.value }),
);

let heroObserver = null;
let hideObserver = null;

// The rules (display: contents rows, the half-visible button at the fold, hide sections) live in
// utils/phoneStickyCta.ts with their reasons, and tests/tracking/phoneStickyCta.test.ts covers them.
onMounted(() => {
  const target = heroTarget(document.querySelector(props.watch), (el) => getComputedStyle(el).display);
  if (target) {
    heroObserver = new IntersectionObserver(
      ([entry]) => {
        heroOnScreen.value = heroOnScreenFrom(entry, target.byButton);
      },
      target.byButton ? { threshold: HERO_BUTTON_THRESHOLDS } : undefined,
    );
    heroObserver.observe(target.el);
  }
  const hideTracker = createHideTracker();
  hideObserver = new IntersectionObserver((entries) => {
    overOwnCta.value = hideTracker(entries);
  });
  document.querySelectorAll(props.hide).forEach((el) => hideObserver.observe(el));
});

onBeforeUnmount(() => {
  heroObserver?.disconnect();
  hideObserver?.disconnect();
});
</script>

<style scoped>
.phone-sticky-cta {
  display: none;
}

@media (max-width: 767px) {
  .phone-sticky-cta {
    position: fixed;
    z-index: 40;
    right: 0;
    bottom: 0;
    left: 0;
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    gap: 0.6rem;
    border-top: 1px solid rgb(15 23 42 / 0.08);
    background: rgb(255 255 255 / 0.92);
    box-shadow: 0 -12px 30px rgb(15 23 42 / 0.1);
    padding: 0.7rem 1rem calc(0.7rem + env(safe-area-inset-bottom));
    pointer-events: none;
    transform: translateY(110%);
    transition: transform 0.32s cubic-bezier(0.2, 0.8, 0.2, 1);
    -webkit-backdrop-filter: blur(14px);
    backdrop-filter: blur(14px);
  }

  .dark .phone-sticky-cta {
    border-color: rgb(148 163 184 / 0.16);
    background: rgb(2 6 23 / 0.9);
    box-shadow: 0 -12px 30px rgb(0 0 0 / 0.4);
  }

  .phone-sticky-cta.on {
    pointer-events: auto;
    transform: none;
  }

  /* TrialDisclosure has two root nodes, so a plain scoped class would not reach it. */
  .phone-sticky-cta :deep(.psc-note) {
    grid-column: 1 / -1;
    margin: 0;
  }

  .psc-secondary,
  .psc-primary {
    display: inline-flex;
    min-height: 2.9rem;
    align-items: center;
    justify-content: center;
    /* px: buttons stay 8px at 125% phone text (0.6rem became 12px). */
    border-radius: 8px;
    font-size: 0.92rem;
    font-weight: 600;
    line-height: 1.15;
    /* Long translations ("Mit dem Vertrieb sprechen") wrap; keep both lines centred. */
    text-align: center;
    padding: 0 1.1rem;
  }

  .psc-secondary {
    border: 1px solid rgb(203 213 225);
    color: #0f172a;
  }

  .dark .psc-secondary {
    border-color: rgb(51 65 85);
    color: #e2e8f0;
  }

  .psc-primary {
    background: #0f172a;
    color: #fff;
  }

  .dark .psc-primary {
    background: #f8fafc;
    color: #0f172a;
  }
}

@media (prefers-reduced-motion: reduce) {
  .phone-sticky-cta {
    transition: none;
  }
}
</style>
